import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import { Modal } from '../../design-system/Modal';
import {
  CheckCircle2,
  MessageSquare,
  Zap,
  AtSign,
  AlertTriangle,
  Bell,
  Trash2,
  CheckCheck,
  Settings2,
  Mail,
  Smartphone
} from 'lucide-react';

export const InboxPage = () => {
  const { allTasks = [], setSelectedTask } = useWorkspace();
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'unread', 'mentions', 'assignments', 'blocked'
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  const [preferences, setPreferences] = useState({
    emailDigests: true,
    pushMentions: true,
    taskAssignments: true,
    blockedAlerts: true
  });

  const [notifications, setNotifications] = useState([
    {
      id: 'notif_1',
      title: 'Task Assigned',
      description: 'You were assigned to FOR-101 (Implement Neomorphic Dual-Shadow Theme Engine).',
      taskId: 'FOR-101',
      time: '25 mins ago',
      type: 'assignment',
      read: false
    },
    {
      id: 'notif_2',
      title: 'Mentioned in Discussion',
      description: 'Sarah Connor: "@Rohith please review the design tokens spec draft for dark mode."',
      taskId: 'FOR-101',
      time: '1 hour ago',
      type: 'mention',
      read: false
    },
    {
      id: 'notif_3',
      title: 'Task Blocked Warning',
      description: 'FOR-102 (Dynamic Module Routing) is waiting on prerequisite FOR-101.',
      taskId: 'FOR-102',
      time: 'Yesterday',
      type: 'blocked',
      read: false
    },
    {
      id: 'notif_4',
      title: 'Sprint 1 Milestone Milestone',
      description: 'Sprint 1 Acceptance milestone moved to 85% completion.',
      time: 'Yesterday',
      type: 'milestone',
      read: true
    }
  ]);

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const toggleReadStatus = (e, notifId) => {
    e.stopPropagation();
    setNotifications((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, read: !n.read } : n))
    );
  };

  const deleteNotification = (e, notifId) => {
    e.stopPropagation();
    setNotifications((prev) => prev.filter((n) => n.id !== notifId));
  };

  const clearRead = () => {
    setNotifications((prev) => prev.filter((n) => !n.read));
  };

  const handleOpenTask = (taskId) => {
    if (!taskId) return;
    const foundTask = allTasks.find((t) => t.id === taskId);
    if (foundTask) {
      setSelectedTask(foundTask);
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === 'unread') return !n.read;
    if (activeTab === 'mentions') return n.type === 'mention';
    if (activeTab === 'assignments') return n.type === 'assignment';
    if (activeTab === 'blocked') return n.type === 'blocked';
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getTypeIcon = (type) => {
    switch (type) {
      case 'assignment':
        return <Zap size={18} color="var(--accent-teal)" />;
      case 'mention':
        return <AtSign size={18} color="var(--color-purple)" />;
      case 'blocked':
        return <AlertTriangle size={18} color="var(--color-warning)" />;
      case 'comment':
        return <MessageSquare size={18} color="var(--color-info)" />;
      case 'milestone':
      default:
        return <CheckCircle2 size={18} color="var(--color-success)" />;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Inbox</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
              Actionable updates, task assignments, and mentions
            </p>
          </div>
          {unreadCount > 0 && (
            <Badge variant="teal" size="sm">
              {unreadCount} Unread
            </Badge>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setIsPreferencesOpen(true)}
            style={{
              padding: '7px 10px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--surface-recessed)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}
            title="Notification Preferences"
          >
            <Settings2 size={14} />
            <span>Preferences</span>
          </button>

          <Button
            variant="secondary"
            size="sm"
            icon={<CheckCheck size={14} />}
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark all read
          </Button>

          <Button variant="ghost" size="sm" onClick={clearRead}>
            Clear Read
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-divider)', gap: '8px' }}>
        {[
          { id: 'all', label: 'All Updates', count: notifications.length },
          { id: 'unread', label: 'Unread', count: unreadCount },
          { id: 'mentions', label: 'Mentions', count: notifications.filter((n) => n.type === 'mention').length },
          { id: 'assignments', label: 'Assignments', count: notifications.filter((n) => n.type === 'assignment').length },
          { id: 'blocked', label: 'Blocked Alerts', count: notifications.filter((n) => n.type === 'blocked').length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-teal)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--accent-teal)' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>{tab.label}</span>
            <Badge size="xs" variant={activeTab === tab.id ? 'teal' : 'neutral'}>
              {tab.count}
            </Badge>
          </button>
        ))}
      </div>

      {/* Notifications List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filteredNotifications.map((n) => (
          <div
            key={n.id}
            onClick={() => handleOpenTask(n.taskId)}
            style={{
              padding: '16px 20px',
              backgroundColor: n.read ? 'var(--surface-card)' : 'var(--surface-recessed)',
              boxShadow: n.read ? 'var(--shadow-raised-sm)' : 'var(--shadow-recessed-sm)',
              borderRadius: 'var(--radius-lg)',
              border: n.read ? '1px solid var(--border-subtle)' : '1px solid rgba(35, 190, 179, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              cursor: n.taskId ? 'pointer' : 'default',
              transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            className="interactive-raised"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
              <div
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-raised-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {getTypeIcon(n.type)}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {n.title}
                  </span>
                  {n.taskId && (
                    <Badge variant="recessed" size="xs">
                      {n.taskId}
                    </Badge>
                  )}
                  {!n.read && (
                    <span
                      style={{
                        width: '7px',
                        height: '7px',
                        borderRadius: '999px',
                        backgroundColor: 'var(--accent-teal)'
                      }}
                    />
                  )}
                </div>

                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                  {n.description}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{n.time}</span>

              <button
                onClick={(e) => toggleReadStatus(e, n.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: n.read ? 'var(--text-muted)' : 'var(--accent-teal)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
                title={n.read ? 'Mark unread' : 'Mark read'}
              >
                {n.read ? 'Unread' : 'Read'}
              </button>

              <button
                onClick={(e) => deleteNotification(e, n.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '4px'
                }}
                title="Dismiss"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}

        {filteredNotifications.length === 0 && (
          <div
            style={{
              padding: '60px 20px',
              textAlign: 'center',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--text-muted)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Bell size={32} color="var(--accent-teal)" style={{ opacity: 0.8 }} />
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              No notifications in this tab
            </div>
            <div style={{ fontSize: '13px' }}>You are completely caught up!</div>
          </div>
        )}
      </div>

      {/* NOTIFICATION PREFERENCES MODAL */}
      <Modal
        isOpen={isPreferencesOpen}
        onClose={() => setIsPreferencesOpen(false)}
        title="Notification Preferences"
        subtitle="Manage communication channels and push frequency"
        footer={
          <Button variant="primary" onClick={() => setIsPreferencesOpen(false)}>
            Save Preferences
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Mail size={16} color="var(--accent-teal)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Email Daily Digest
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Summary of assigned tasks, milestone progress, and mentions
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.emailDigests}
              onChange={() =>
                setPreferences((prev) => ({ ...prev, emailDigests: !prev.emailDigests }))
              }
              style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AtSign size={16} color="var(--color-purple)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Immediate @Mention Push
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Instant alerts when teammates mention you in comments
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.pushMentions}
              onChange={() =>
                setPreferences((prev) => ({ ...prev, pushMentions: !prev.pushMentions }))
              }
              style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Zap size={16} color="var(--color-info)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Task Assignment Alerts
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Notify immediately when a project task is assigned to you
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.taskAssignments}
              onChange={() =>
                setPreferences((prev) => ({ ...prev, taskAssignments: !prev.taskAssignments }))
              }
              style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
            />
          </label>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Smartphone size={16} color="var(--color-warning)" />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  Blocked Task Warnings
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Alert when a dependency blocks one of your assigned tasks
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.blockedAlerts}
              onChange={() =>
                setPreferences((prev) => ({ ...prev, blockedAlerts: !prev.blockedAlerts }))
              }
              style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
            />
          </label>
        </div>
      </Modal>
    </div>
  );
};
